import React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Filter, X } from 'lucide-react';
import { format } from 'date-fns';
import type { AssetsFilters } from '@/hooks/useAssetsUnifiedData';
import { formatAssetClass } from '@/utils/assetClassDisplay';
import { getCurrencyLabel } from '@/lib/currency';

interface AssetsAdvancedFiltersProps {
  filters: AssetsFilters;
  onFiltersChange: (filters: Partial<AssetsFilters>) => void;
  onReset: () => void;
  availableCurrencies: string[];
}

export function AssetsAdvancedFilters({
  filters,
  onFiltersChange,
  onReset,
  availableCurrencies
}: AssetsAdvancedFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Asset type options including multi_asset
  const assetTypeOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'equity', label: 'Equity' },
    { value: 'fixed_income', label: 'Fixed Income' },
    { value: 'alternative', label: 'Alternative' },
    { value: 'multi_asset', label: 'Multi-Asset' },
  ];

  const sourceTypeOptions = [
    { value: 'manual', label: 'Manual Input' },
    { value: 'connected', label: 'Connected' },
    { value: 'account', label: 'Account Balance' },
  ];

  const hasActiveFilters = 
    (filters.assetTypes?.length || 0) > 0 ||
    (filters.currencies?.length || 0) > 0 ||
    (filters.sourceTypes?.length || 0) > 0 ||
    filters.valueRange.min !== null ||
    filters.valueRange.max !== null ||
    filters.dateRange.start !== null ||
    filters.dateRange.end !== null;

  const activeFilterCount = [
    (filters.assetTypes?.length || 0) > 0,
    (filters.currencies?.length || 0) > 0,
    (filters.sourceTypes?.length || 0) > 0,
    filters.valueRange.min !== null || filters.valueRange.max !== null,
    filters.dateRange.start !== null || filters.dateRange.end !== null,
  ]?.filter(Boolean)?.length || 0;

  const handleAssetTypeChange = (assetType: string, checked: boolean) => {
    const newAssetTypes = checked
      ? [...filters.assetTypes, assetType as any]
      : filters.assetTypes.filter(type => type !== assetType);
    
    onFiltersChange({ assetTypes: newAssetTypes });
  };

  const handleCurrencyChange = (currency: string, checked: boolean) => {
    const newCurrencies = checked
      ? [...filters.currencies, currency]
      : filters.currencies.filter(c => c !== currency);
    
    onFiltersChange({ currencies: newCurrencies });
  };

  const handleSourceTypeChange = (sourceType: string, checked: boolean) => {
    const newSourceTypes = checked
      ? [...filters.sourceTypes, sourceType as any]
      : filters.sourceTypes.filter(type => type !== sourceType);
    
    onFiltersChange({ sourceTypes: newSourceTypes });
  };

  const handleValueRangeChange = (field: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : parseFloat(value);
    onFiltersChange({
      valueRange: {
        ...filters.valueRange,
        [field]: numValue
      }
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', date: Date | undefined) => {
    onFiltersChange({
      dateRange: {
        ...filters.dateRange,
        [field]: date || null
      }
    });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Advanced Filters</h4>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onReset();
                  setIsOpen(false);
                }}
                className="h-8 px-2 text-xs"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Asset Types */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Asset Types</Label>
            <div className="space-y-2">
              {assetTypeOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`asset-type-${option.value}`}
                    checked={filters.assetTypes.includes(option.value as any)}
                    onCheckedChange={(checked) =>
                      handleAssetTypeChange(option.value, checked as boolean)
                    }
                  />
                  <Label
                    htmlFor={`asset-type-${option.value}`}
                    className="text-sm font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Currencies */}
          {(availableCurrencies?.length || 0) > 1 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Currencies</Label>
              <div className="space-y-2">
                {availableCurrencies.map((currency) => (
                  <div key={currency} className="flex items-center space-x-2">
                    <Checkbox
                      id={`currency-${currency}`}
                      checked={filters.currencies.includes(currency)}
                      onCheckedChange={(checked) =>
                        handleCurrencyChange(currency, checked as boolean)
                      }
                    />
                    <Label
                      htmlFor={`currency-${currency}`}
                      className="text-sm font-normal"
                    >
                      {getCurrencyLabel(currency)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Source Types */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Source</Label>
            <div className="space-y-2">
              {sourceTypeOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`source-${option.value}`}
                    checked={filters.sourceTypes.includes(option.value as any)}
                    onCheckedChange={(checked) =>
                      handleSourceTypeChange(option.value, checked as boolean)
                    }
                  />
                  <Label
                    htmlFor={`source-${option.value}`}
                    className="text-sm font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Value Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Value Range</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.valueRange.min?.toString() || ''}
                  onChange={(e) => handleValueRangeChange('min', e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.valueRange.max?.toString() || ''}
                  onChange={(e) => handleValueRangeChange('max', e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date Added</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange.start ? (
                      format(filters.dateRange.start, "MMM dd")
                    ) : (
                      <span className="text-muted-foreground">From</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateRange.start || undefined}
                    onSelect={(date) => handleDateRangeChange('start', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange.end ? (
                      format(filters.dateRange.end, "MMM dd")
                    ) : (
                      <span className="text-muted-foreground">To</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateRange.end || undefined}
                    onSelect={(date) => handleDateRangeChange('end', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className="pt-2 border-t">
              <div className="flex flex-wrap gap-1">
                {filters.assetTypes.map((type) => (
                  <Badge key={type} variant="secondary" className="text-xs">
                    {formatAssetClass(type)}
                    <button
                      onClick={() => handleAssetTypeChange(type, false)}
                      className="ml-1 hover:bg-secondary-foreground/20 rounded-full"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {filters.currencies.map((currency) => (
                  <Badge key={currency} variant="secondary" className="text-xs">
                    {getCurrencyLabel(currency)}
                    <button
                      onClick={() => handleCurrencyChange(currency, false)}
                      className="ml-1 hover:bg-secondary-foreground/20 rounded-full"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
